import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { Card, CardContent, Box, Typography, Avatar, Chip, useTheme } from '@mui/material';
import { Button } from '@ui/components/Button';
import { Dropdown } from '@ui/components/Dropdown';
const CardComponent = ({ title, subtitle, description, leftIcon, rightIcon, badge, variant = 'people', onClick, rightAction, buttonText, buttonOnClick, dropdownOptions, dropdownOnSelect, dropdownPlaceholder, eventDate, responses, companies, rightAvatar, className, sx }) => {
    const theme = useTheme();
    const getVariantStyles = () => {
        switch (variant) {
            case 'people':
                return {
                    card: {
                        borderRadius: theme.spacing(2),
                        border: `1px solid ${theme.palette.divider}`,
                        backgroundColor: theme.palette.background.paper,
                        boxShadow: theme.shadows[1],
                        '&:hover': {
                            boxShadow: theme.shadows[2],
                            transform: 'translateY(-1px)',
                            transition: 'all 0.2s ease-in-out'
                        }
                    },
                    avatar: {
                        width: 40,
                        height: 40,
                        backgroundColor: theme.palette.primary.main,
                        color: theme.palette.common.white
                    }
                };
            case 'event':
                return {
                    card: {
                        borderRadius: theme.spacing(3),
                        border: `1px solid ${theme.palette.divider}`,
                        backgroundColor: theme.palette.background.paper,
                        boxShadow: theme.shadows[1],
                        '&:hover': {
                            boxShadow: theme.shadows[2],
                            transform: 'translateY(-1px)',
                            transition: 'all 0.2s ease-in-out'
                        }
                    },
                    avatar: {
                        width: 32,
                        height: 32,
                        backgroundColor: theme.palette.primary.main,
                        color: theme.palette.common.white,
                        borderRadius: theme.spacing(1)
                    },
                    rightAvatar: {
                        width: 32,
                        height: 32,
                        borderRadius: theme.spacing(1)
                    }
                };
            case 'task':
                return {
                    card: {
                        borderRadius: theme.spacing(2),
                        border: `1px solid ${theme.palette.divider}`,
                        background: `linear-gradient(135deg, ${theme.palette.success.light}10, ${theme.palette.success.main}05)`,
                        backgroundColor: theme.palette.background.paper,
                        '&:hover': {
                            boxShadow: theme.shadows[2],
                            transform: 'translateY(-1px)',
                            transition: 'all 0.2s ease-in-out'
                        }
                    },
                    avatar: {
                        width: 40,
                        height: 40,
                        backgroundColor: theme.palette.success.main,
                        color: theme.palette.common.white
                    }
                };
            default:
                return {
                    card: {
                        borderRadius: theme.spacing(2),
                        border: `1px solid ${theme.palette.divider}`,
                        backgroundColor: theme.palette.background.paper,
                        '&:hover': {
                            boxShadow: theme.shadows[2],
                            transform: 'translateY(-1px)',
                            transition: 'all 0.2s ease-in-out'
                        }
                    },
                    avatar: {
                        width: 40,
                        height: 40,
                        backgroundColor: theme.palette.primary.main,
                        color: theme.palette.common.white
                    }
                };
        }
    };
    const variantStyles = getVariantStyles();
    return (_jsx(Card, { className: className, sx: {
            cursor: onClick ? 'pointer' : 'default',
            ...variantStyles.card,
            ...sx
        }, onClick: onClick, children: _jsx(CardContent, { sx: { padding: theme.spacing(2) }, children: variant === 'people' ? (
            // People card layout
            _jsxs(Box, { sx: {
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    gap: theme.spacing(2)
                }, children: [_jsxs(Box, { sx: {
                            display: 'flex',
                            alignItems: 'center',
                            gap: theme.spacing(2),
                            flex: 1,
                            minWidth: 0
                        }, children: [leftIcon && _jsx(Avatar, { sx: variantStyles.avatar, children: leftIcon }), _jsxs(Box, { sx: { flex: 1, minWidth: 0 }, children: [_jsx(Typography, { variant: 'subtitle1', sx: {
                                            fontWeight: theme.typography.fontWeightMedium,
                                            color: theme.palette.text.primary,
                                            fontSize: theme.typography.pxToRem(16),
                                            lineHeight: 1.3,
                                            marginBottom: theme.spacing(0.5)
                                        }, children: title }), subtitle && (_jsxs(Typography, { variant: 'body2', sx: {
                                            color: theme.palette.text.secondary,
                                            fontSize: '14px',
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: theme.spacing(1)
                                        }, children: [_jsx(Box, { component: 'span', sx: {
                                                    width: 16,
                                                    height: 16,
                                                    borderRadius: '50%',
                                                    backgroundColor: theme.palette.primary.main,
                                                    display: 'inline-flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'center',
                                                    fontSize: '10px',
                                                    color: theme.palette.common.white
                                                }, children: "\u2713" }), subtitle] }))] })] }), rightAction === 'button' && buttonText && (_jsx(Button, { variant: 'outlined', size: 'small', onClick: buttonOnClick, sx: { flexShrink: 0 }, children: buttonText })), rightAction === 'dropdown' && dropdownOptions && (_jsx(Dropdown, { options: dropdownOptions, onSelect: dropdownOnSelect, placeholder: dropdownPlaceholder, sx: { flexShrink: 0, minWidth: 150 }, children: dropdownPlaceholder }))] })) : variant === 'event' ? (
            // Event card layout
            _jsxs(Box, { sx: {
                    display: 'flex',
                    alignItems: 'flex-start',
                    justifyContent: 'space-between',
                    gap: theme.spacing(2)
                }, children: [_jsxs(Box, { sx: {
                            display: 'flex',
                            alignItems: 'flex-start',
                            gap: theme.spacing(2),
                            flex: 1,
                            minWidth: 0
                        }, children: [leftIcon && _jsx(Avatar, { sx: variantStyles.avatar, children: leftIcon }), _jsxs(Box, { sx: { flex: 1, minWidth: 0 }, children: [_jsxs(Box, { sx: {
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: theme.spacing(1),
                                            marginBottom: theme.spacing(0.5)
                                        }, children: [_jsx(Typography, { variant: 'h6', sx: {
                                                    fontWeight: 600,
                                                    color: theme.palette.text.primary,
                                                    fontSize: '16px',
                                                    lineHeight: 1.3
                                                }, children: title }), responses && (_jsxs(Typography, { sx: {
                                                    fontSize: '12px',
                                                    fontWeight: 500,
                                                    color: theme.palette.success.main,
                                                    backgroundColor: theme.palette.success.main + '20',
                                                    padding: theme.spacing(0.25, 0.75),
                                                    borderRadius: theme.spacing(0.5)
                                                }, children: [responses, " responses"] }))] }), eventDate && (_jsx(Typography, { variant: 'body2', sx: {
                                            color: theme.palette.text.secondary,
                                            fontSize: '14px',
                                            marginBottom: theme.spacing(0.5)
                                        }, children: eventDate })), companies && companies.length > 0 && (_jsx(Typography, { variant: 'body2', sx: {
                                            color: theme.palette.text.secondary,
                                            fontSize: '12px',
                                            lineHeight: 1.4,
                                            display: '-webkit-box',
                                            WebkitLineClamp: 2,
                                            WebkitBoxOrient: 'vertical',
                                            overflow: 'hidden'
                                        }, children: companies.join(', ') }))] })] }), rightAvatar && _jsx(Avatar, { src: rightAvatar, sx: variantStyles.rightAvatar })] })) : (
            // Default card layout for event and task
            _jsxs(Box, { sx: {
                    display: 'flex',
                    alignItems: 'flex-start',
                    gap: theme.spacing(2)
                }, children: [leftIcon && _jsx(Avatar, { sx: variantStyles.avatar, children: leftIcon }), _jsxs(Box, { sx: { flex: 1, minWidth: 0 }, children: [_jsxs(Box, { sx: {
                                    display: 'flex',
                                    justifyContent: 'space-between',
                                    alignItems: 'flex-start',
                                    marginBottom: theme.spacing(0.5)
                                }, children: [_jsx(Typography, { variant: 'subtitle1', sx: {
                                            fontWeight: 600,
                                            color: theme.palette.text.primary,
                                            fontSize: '16px',
                                            lineHeight: 1.3,
                                            overflow: 'hidden',
                                            textOverflow: 'ellipsis',
                                            whiteSpace: 'nowrap'
                                        }, children: title }), badge && (_jsx(Chip, { label: badge, size: 'small', sx: {
                                            fontSize: '11px',
                                            height: 20,
                                            backgroundColor: theme.palette.error.main,
                                            color: theme.palette.common.white,
                                            '& .MuiChip-label': {
                                                padding: theme.spacing(0, 1)
                                            }
                                        } }))] }), subtitle && (_jsx(Typography, { variant: 'body2', sx: {
                                    color: theme.palette.text.secondary,
                                    fontSize: '14px',
                                    marginBottom: theme.spacing(0.5),
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    whiteSpace: 'nowrap'
                                }, children: subtitle })), description && (_jsx(Typography, { variant: 'body2', sx: {
                                    color: theme.palette.text.secondary,
                                    fontSize: '13px',
                                    lineHeight: 1.4,
                                    display: '-webkit-box',
                                    WebkitLineClamp: 2,
                                    WebkitBoxOrient: 'vertical',
                                    overflow: 'hidden'
                                }, children: description }))] }), rightIcon && (_jsx(Box, { sx: {
                            display: 'flex',
                            alignItems: 'center',
                            color: theme.palette.text.secondary
                        }, children: rightIcon }))] })) }) }));
};
export default CardComponent;
